import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Stethoscope, Mail, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      const redirect = sp.get("next");
      if (redirect) nav(redirect);
      else if (u.role === "admin") nav("/admin");
      else if (u.role === "doctor") nav("/doctor/dashboard");
      else nav("/patient/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto grid min-h-[80vh] max-w-6xl grid-cols-1 items-center gap-10 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:px-8">
      <div className="hidden lg:block">
        <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-1.5 text-xs font-semibold text-blue-800">
          <Stethoscope className="h-3.5 w-3.5" /> Secure patient portal
        </div>
        <h1 className="mt-5 text-4xl font-extrabold tracking-tight text-slate-900">Welcome back to <span className="bg-gradient-to-r from-blue-700 to-teal-600 bg-clip-text text-transparent">Doctor+</span></h1>
        <p className="mt-4 max-w-md text-slate-600">Access your bookings, prescriptions, reviews, and manage appointments for your family — all in one place.</p>
        <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm">
          <div className="font-semibold text-slate-900">Demo credentials</div>
          <div className="mt-2 space-y-1 text-slate-600 font-mono text-xs">
            <div>Patient — patient@doctor-plus.in / Patient@123</div>
            <div>Doctor — ananya@doctor-plus.in / Doctor@123</div>
            <div>Admin — admin@doctor-plus.in / Admin@12345</div>
          </div>
        </div>
      </div>

      <div className="card p-8">
        <h2 className="text-2xl font-extrabold text-slate-900">Sign in</h2>
        <p className="mt-1 text-sm text-slate-600">Use your email and password</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-700">Email</label>
            <div className="relative mt-2">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input data-testid="login-email" required type="email" className="input-field pl-9" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">Password</label>
            <div className="relative mt-2">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input data-testid="login-password" required type="password" className="input-field pl-9" value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
          </div>
          <button data-testid="login-submit" disabled={submitting} className="btn-primary w-full">
            {submitting ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <div className="mt-6 text-center text-sm text-slate-600">
          New here?{" "}
          <Link data-testid="go-register" to="/register" className="font-semibold text-blue-700 hover:text-blue-800">Create an account</Link>
        </div>
      </div>
    </div>
  );
}
